import { useCustomTestCreateWizard } from 'pages/custom_tests_v1/components/custom_test_create_wizard/context';
import { Form, ListGroup, ListGroupItem } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

// Categories that ship both In-App and In-Browser question pools; only these expose the
// delivery selector below.
// TODO(custom-test-magic-ids): drive this off category metadata instead of a hardcoded id list
// (see GetTestTypeForCategory on the backend).
const HYBRID_DELIVERY_CATEGORIES = [21, 40];

// Neutral GMetrix mark shown when a category/product icon URL is missing or 404s on the CDN.
const FALLBACK_LOGO = 'https://content.gmetrix.net/images/GMetrixLogoBlue.png';

// The `Color` column is a raw CSS fragment (e.g. "315895; background: linear-gradient(...)"),
// not a bare hex. Take only the leading solid colour, and only tint SVG (white-glyph) icons —
// full-colour PNG logos must stay on a transparent background.
const tileBackground = (iconUrl: string, color: string): string => iconUrl.includes('svg') ? `#${(color || '').split(';')[0].trim() || 'ffffff'}` : 'transparent';

const handleIconError = (e: React.SyntheticEvent<HTMLImageElement>) => {

    const img = e.currentTarget;

    if (!img.src.endsWith('GMetrixLogoBlue.png')) {

        img.src = FALLBACK_LOGO;
        img.style.backgroundColor = 'transparent';

    }

};

export const ProductSelectStep = props => {

    const { categories, categoryId, deliveryType, handleDeliveryChange, handleToggleCategory, handleToggleProduct,
        loadingProducts: loading, productIds, products } = useCustomTestCreateWizard();
    const { formatMessage } = useIntl();

    return (
        <div className="d-flex flex-column gap-3 py-3">

            <section>
                <h5>
                    {<FormattedMessage defaultMessage="Select Category" id="select_category.d9d07241" />}
                </h5>

                <ListGroup className={'rounded border overflow-y-auto'} style={{ maxHeight: 400 }} variant={'flush'}>
                    {categories.map((category, key) => (
                        <ListGroupItem
                            action
                            active={categoryId === category?.Id}
                            className={'d-flex gap-5 align-items-center'}
                            key={key}
                            onClick={() => handleToggleCategory(category.Id)}
                        >
                            {category.IconURL && (
                                <img
                                    style={{
                                        backgroundColor: tileBackground(category.IconURL, category.Color),
                                        height: 40,
                                        objectFit: 'contain',
                                        overflow: 'clip',
                                        width: 60
                                    }}
                                    alt={category.Name}
                                    className="rounded p-1"
                                    onError={handleIconError}
                                    src={category.IconURL}
                                />
                            )}
                            <small className={'fw-bold'}>
                                {category.Name}
                            </small>
                        </ListGroupItem>
                    ))}
                </ListGroup>
            </section>

            <section className="d-flex flex-column mb-2">
                <h5>
                    {<FormattedMessage defaultMessage="Select Products" id="select_products.6841dc08" />}
                </h5>

                {HYBRID_DELIVERY_CATEGORIES.includes(categoryId) && (
                    <div className="d-flex align-items-center gap-4 mb-2">
                        <span className="fw-bold">
                            {<FormattedMessage defaultMessage="Question Type" id="question_type.4b121765" />}
                        </span>
                        <Form.Check
                            checked={deliveryType === 3}
                            id="delivery-browser"
                            label={formatMessage({ id: 'browser_based.9d2140b1', defaultMessage: 'Browser Based' })}
                            name="deliveryType"
                            onChange={() => handleDeliveryChange(3)}
                            type="radio"
                        />
                        <Form.Check
                            checked={deliveryType === 1}
                            id="delivery-inapp"
                            label={formatMessage({ id: 'live_in_the_app.6c9f2a02', defaultMessage: 'Live in the App' })}
                            name="deliveryType"
                            onChange={() => handleDeliveryChange(1)}
                            type="radio"
                        />
                    </div>
                )}

                {loading ?
                    (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">
                                    {<FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />}
                                </span>
                            </div>
                        </div>
                    )
                    :
                    (
                        <>
                            {products?.length ?
                                (
                                    <ListGroup className={'rounded border overflow-y-auto'} style={{ maxHeight: 400 }} variant={'flush'}>
                                        {products.map((product, key) => (
                                            <ListGroupItem
                                                action
                                                active={productIds.includes(product.Id)}
                                                className={`d-flex gap-5 align-items-center ${productIds.includes(product.Id) ? 'border-white' : ''}`}
                                                key={key}
                                                onClick={() => handleToggleProduct(product.Id)}
                                            >
                                                {product.IconURL && (
                                                    <img
                                                        style={{
                                                            backgroundColor: tileBackground(product.IconURL, product.Color),
                                                            height: 40,
                                                            objectFit: 'contain',
                                                            overflow: 'clip',
                                                            width: 60
                                                        }}
                                                        alt={product.Name}
                                                        className="rounded p-1"
                                                        onError={handleIconError}
                                                        src={product.IconURL}
                                                    />
                                                )}
                                                <small className="fw-bold">
                                                    {product.Name}
                                                </small>
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                )
                                :
                                (
                                    <div className={'d-flex justify-content-center p-5 border rounded'}>
                                        <span className={'text-muted'}>
                                            {<FormattedMessage defaultMessage="Select a category" id="select_a_category.87f0b32d" />}
                                        </span>
                                    </div>
                                )
                            }
                        </>
                    )}
            </section>

        </div>
    );

};
